/**
 * Listas de LEITURA — texto corrido em linhas, sem alvo de toque.
 *
 * Ingrediente, passo de preparo, regra de uma lista. Nenhum desses leva a tela
 * nenhuma, então `Lista` (que é botão e termina em chevron) prometeria um toque
 * que não existe, e `Dados` (rótulo à esquerda, valor à direita) não serve para
 * uma frase.
 *
 * Nasceram em components/bonus/Conteudo.tsx, para as três páginas de bônus, e
 * subiram para cá quando a receita e a página de doces pediram as mesmas duas.
 */

import type { LucideIcon } from "lucide-react";

const GUTTER = { paddingLeft: "var(--gutter)", paddingRight: "var(--gutter)" };

/** Itens sem ordem: ingredientes, regras, avisos. Marcador tipográfico. */
export function ListaTexto({
  itens,
  /** Dentro de um cartão a margem lateral já veio do cartão. */
  embutido,
  /**
   * Um ícone por item, na mesma ordem de `itens`. Usado na linha de
   * ingrediente, onde a família do alimento (ave, laticínio, farinha) ajuda a
   * varrer a lista sem ler cada palavra. Sem ícone, o marcador é o travessão.
   */
  icones,
}: {
  itens: readonly string[];
  embutido?: boolean;
  icones?: readonly (LucideIcon | undefined)[];
}) {
  return (
    <ul style={{ ...(embutido ? null : GUTTER), display: "grid", gap: 12 }}>
      {itens.map((item, i) => {
        const Icone = icones?.[i];
        return (
          <li
            key={`${i}-${item}`}
            className="flex gap-3 leading-relaxed"
            style={{ fontSize: "var(--t-corpo)" }}
          >
            {Icone ? (
              <Icone
                className="mt-0.5 h-5 w-5 shrink-0"
                strokeWidth={2}
                style={{ color: "var(--tinta-apoio)" }}
              />
            ) : (
              <span aria-hidden="true" style={{ color: "var(--tinta-fraca)" }}>
                —
              </span>
            )}
            <span style={{ color: "var(--tinta-apoio)" }}>{item}</span>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Itens em ordem: o modo de preparo.
 *
 * O número fica fora do texto e alinhado, com `tabular-nums`, porque quem está
 * cozinhando volta os olhos para a tela e precisa reencontrar o passo — e
 * reencontrar o passo 7 é achar o número, não reler a frase.
 */
export function ListaNumerada({
  itens,
  embutido,
}: {
  itens: readonly string[];
  embutido?: boolean;
}) {
  return (
    <ol style={{ ...(embutido ? null : GUTTER), display: "grid", gap: 16 }}>
      {itens.map((item, i) => (
        <li
          key={`${i}-${item}`}
          className="flex gap-4 leading-relaxed"
          style={{ fontSize: "var(--t-corpo)" }}
        >
          <span
            className="shrink-0 font-bold tabular-nums"
            style={{ minWidth: "1.6em", fontSize: "var(--t-linha)" }}
          >
            {i + 1}.
          </span>
          <span style={{ color: "var(--tinta-apoio)" }}>{item}</span>
        </li>
      ))}
    </ol>
  );
}
